import React from "react";
import PropTypes from "prop-types";
import {
  IconButton,
  InputAdornment,
  ThemeProvider,
  TextField,
  createTheme,
} from "@material-ui/core";
import {
  Button,
  FullPageSpinner,
  NotificationBar,
  SplitDialog,
  Typography,
} from "@invosys/invosys-ui";
import PasswordStrengthMeter, {
  getPasswordStrength,
} from "@invosys/invosys-ui/components/LoginAvatar/PasswordStrengthMeter";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import theme from "../styles/theme";
import getBranding from "../actions/getBranding";
import { getAPIEndpoint } from "../actions/getHostname";

// require("./resetpassword.css");

export default class ResetPassword extends React.Component {
  constructor() {
    super();
    this.state = {
      customTheme: createTheme(theme),
      hostname: window.location.hostname,
      resetToken: "",
      errors: {},
    };
    this.notifications = React.createRef();
  }

  componentDidMount = () => {
    getBranding(null, window.location.hostname).then((colorRes) => {
      if (colorRes.primaryColor) {
        theme.palette.primary.main = colorRes.primaryColor;
      }
      if (colorRes.secondaryColor) {
        theme.palette.secondary.main = colorRes.secondaryColor;
      }
      if (colorRes.fontFamily) {
        theme.typography = { fontFamily: colorRes.fontFamily };
      }
      this.setState({ customTheme: createTheme(theme) });
    });

    const resetToken = (
      (window.location.search || "?reset=")
        .substring(1)
        .split("&")
        .find((param) => param.startsWith("reset")) || "reset="
    ).split("=")[1];
    this.setState({ resetToken, validating: resetToken }, () => {
      if (this.state.resetToken) {
        const url = `${getAPIEndpoint()}/validate-token?token=${this.state.resetToken}`;
        fetch(url, {
          headers: {
            "content-type": "application/json",
          },
          method: "GET",
        })
          .then((res) => res.json())
          .then((res) => {
            if (res.error) {
              throw new Error(res.error);
            }
            this.setState(
              { validating: false, isAdministrator: res.admin },
              () => {},
            );
          })
          .catch((error) => {
            console.error(error);
            this.setState({ validating: false }, () => {
              this.errorHandler(`${error}`);
            });
          });
      }
    });
  };

  setField = (event) => {
    const { name, value } = event.target;
    this.setState({ [name]: value });
  };

  updateMethod = (evt) => {
    const { name, value } = evt.target;
    const errors = {};
    let { newPassword, confirmPassword } = this.state;
    if (name === "newPassword") {
      newPassword = value;
      if (value !== this.state.confirmPassword) {
        errors.confirmPassword = "Passwords do not match";
      }
      if (getPasswordStrength(value).score < 75) {
        errors.newPassword = "Password not strong enough";
      }
    } else if (name === "confirmPassword") {
      confirmPassword = value;
      if (value !== this.state.newPassword) {
        errors.confirmPassword = "Passwords do not match";
      }
      if (getPasswordStrength(this.state.newPassword || "").score < 75) {
        errors.newPassword = "Password not strong enough";
      }
    }

    const valid =
      newPassword === confirmPassword &&
      getPasswordStrength(newPassword || "").score > 74;

    this.setState({ [name]: value, errors, valid });
  };

  errorHandler = (messageString, actionMethod, actionLabel) => {
    this.notifications.current.enqueueNotification({
      message: (
        <React.Fragment>
          {messageString.split(". ").map((msg, idx) => (
            <Typography key={idx} style={{ textAlign: "left" }}>
              {msg}.
            </Typography>
          ))}
        </React.Fragment>
      ),
      type: "error",
      actionMethod,
      actionLabel,
    });
  };

  popupHandler = (messageString) => {
    this.notifications.current.enqueueNotification({
      message: (
        <React.Fragment>
          {messageString.split(". ").map((msg, idx) => (
            <Typography key={idx} style={{ textAlign: "left" }}>
              {msg}.
            </Typography>
          ))}
        </React.Fragment>
      ),
      type: "info",
    });
  };

  submitRequest = () => {
    const url = `${getAPIEndpoint()}/forgotpassword`;
    const { accountCode, username, hostname } = this.state;
    this.setState({ submitting: true }, () => {
      fetch(url, {
        headers: {
          "content-type": "application/json",
        },
        method: "POST",
        body: JSON.stringify({ accountCode, username, hostname }),
      })
        .then((res) => res.json())
        .then((res) => {
          if (res.error) {
            throw new Error(res.error);
          }
          this.setState({ showDialog: false, submitting: false }, () => {});
          this.popupHandler(
            `Reset password request submitted. Email will be sent to ${username}`,
          );
        })
        .catch((error) => {
          console.error(error);
          this.setState({ showDialog: false, submitting: false }, () => {
            this.errorHandler(
              "Error submitting request. Please try again or contact your account administrator",
            );
          });
        });
    });
  };

  submitNewPassword = () => {
    const url = `${getAPIEndpoint()}/resetpassword`;
    const { resetToken, newPassword } = this.state;
    this.setState({ sending: true }, () => {
      fetch(url, {
        headers: {
          "content-type": "application/json",
        },
        method: "POST",
        body: JSON.stringify({ resetToken, newPassword }),
      })
        .then((res) => res.json())
        .then((res) => {
          if (res.error) {
            throw new Error(res.error);
          }
          this.setState(
            {
              resetToken: null,
              sending: false,
              newPassword: null,
              confirmPassword: null,
            },
            () => {},
          );
          this.popupHandler(
            "Password Reset Successful. You can now log in with your new password.",
          );
        })
        .catch((error) => {
          console.error(error);
          this.setState(
            {
              resetToken: null,
              sending: false,
              newPassword: null,
              confirmPassword: null,
            },
            () => {
              this.errorHandler(
                `${error}`,
                () => {
                  this.setState({ resetToken });
                },
                "Retry",
              );
            },
          );
        });
    });
  };

  render = () => {
    return (
      <ThemeProvider theme={this.state.customTheme}>
        <FullPageSpinner
          active={this.state.validating}
          parentElement={document.querySelector("body")}
        />
        <Typography
          style={{ cursor: "pointer", ...(this.props.promptStyle || {}) }}
          variant="caption"
          onClick={() => this.setState({ showDialog: true })}
        >
          Forgot Password?
        </Typography>
        {this.state.showDialog && (
          <SplitDialog
            maxWidth="sm"
            fullWidth
            open
            title="Forgotten Password"
            dialogContent={
              <React.Fragment>
                <Typography>
                  Please provide your account code and username (usually email
                  address) to reset your password
                </Typography>
                <Typography>
                  An Email will be sent to this address with instructions for
                  how to reset your password.
                </Typography>
                <TextField
                  fullWidth
                  label="Account Code"
                  name="accountCode"
                  onChange={this.setField}
                />
                <TextField
                  fullWidth
                  label="Username"
                  name="username"
                  onChange={this.setField}
                />
              </React.Fragment>
            }
            actions={[
              <Button
                key="cancel"
                onClick={() => this.setState({ showDialog: false })}
                disabled={this.state.submitting}
                variant="outlined"
              >
                Cancel
              </Button>,
              <Button
                key="submit"
                inProgress={this.state.submitting}
                variant="contained"
                color="primary"
                disabled={
                  this.state.submitting ||
                  !this.state.username ||
                  !this.state.accountCode
                }
                onClick={this.submitRequest}
              >
                Submit
              </Button>,
            ]}
          />
        )}
        {this.state.resetToken && (
          <SplitDialog
            maxWidth="sm"
            open={!this.state.validating}
            fullWidth
            title="Enter New Password"
            dialogContent={
              <React.Fragment>
                <Typography>
                  Your new password needs to match the following rules:
                  <ul>
                    <li>
                      Must be at least{" "}
                      {this.state.isAdministrator ? "14" : "12"} characters long
                    </li>
                    <li>Must contain at least 1 upper case character</li>
                    <li>Must contain at least 1 lower case character</li>
                    <li>Must contain at least 1 number</li>
                    <li>
                      Must contain at least 1 of the following special
                      characters
                      <ul>
                        <li>!&quot;£$%^&amp;*()~@&apos;</li>
                      </ul>
                    </li>
                  </ul>
                  It also should not be an easily guessable version of a common
                  word (e.g. Passw0rd!), or a keyboard variation (e.g.
                  Qwerty1!). We recommend the use of passphrases
                </Typography>
                <TextField
                  type={this.state.showNewPassword ? "text" : "password"}
                  label="New Password"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    inputProps: {
                      autoComplete: "new-password",
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            this.setState((prevState) => ({
                              showNewPassword: !prevState.showNewPassword,
                            }))
                          }
                        >
                          {this.state.showNewPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onChange={this.updateMethod}
                  name="newPassword"
                  error={
                    !!(
                      this.state.errors.newPassword &&
                      Object.keys(this.state).includes("newPassword")
                    )
                  }
                  helperText={
                    Object.keys(this.state).includes("newPassword")
                      ? this.state.errors.newPassword
                      : ""
                  }
                />
                {this.state.newPassword ? (
                  <PasswordStrengthMeter
                    passwordLength={this.state.isAdministrator ? 14 : 12}
                    password={this.state.newPassword || ""}
                  />
                ) : (
                  <div style={{ height: "10px" }} />
                )}
                <TextField
                  type={this.state.showConfirmPassword ? "text" : "password"}
                  label="Confirm New Password"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    inputProps: {
                      autoComplete: "new-confirm-password",
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            this.setState((prevState) => ({
                              showConfirmPassword:
                                !prevState.showConfirmPassword,
                            }))
                          }
                        >
                          {this.state.showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onChange={this.updateMethod}
                  name="confirmPassword"
                  error={
                    !!(
                      this.state.errors.confirmPassword &&
                      Object.keys(this.state).includes("confirmPassword")
                    )
                  }
                  helperText={
                    Object.keys(this.state).includes("confirmPassword")
                      ? this.state.errors.confirmPassword
                      : ""
                  }
                />
              </React.Fragment>
            }
            actions={[
              <Button
                key="cancel"
                onClick={() =>
                  this.setState({
                    resetToken: "",
                    newPassword: null,
                    confirmPassword: null,
                  })
                }
                disabled={this.state.sending}
                variant="outlined"
              >
                Cancel
              </Button>,
              <Button
                key="submit"
                variant="contained"
                color="primary"
                disabled={!this.state.valid || this.state.sending}
                inProgress={this.state.sending}
                onClick={this.submitNewPassword}
              >
                Submit
              </Button>,
            ]}
          />
        )}
        <div style={{ textAlign: "left" }} className="leftAlign">
          <NotificationBar
            ref={this.notifications}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          />
        </div>
      </ThemeProvider>
    );
  };
}

ResetPassword.propTypes = { promptStyle: PropTypes.object };
